/**
 * evaService.js
 * EVA - Asistente de IA para GearControl
 * Maneja: presupuestos, agenda, precios de llantas, mensajes
 * Usa caché de 10 min para evitar llamadas repetidas
 */
import { supabase } from '../lib/supabase';

// ─── Cache simple en memoria (TTL 10 min) ───────────────────────────────────
const _cache = new Map();
const CACHE_TTL_MS = 10 * 60 * 1000;

function cacheGet(key) {
  const entry = _cache?.get(key);
  if (!entry) return null;
  if (Date.now() - entry?.ts > CACHE_TTL_MS) {
    _cache?.delete(key);
    return null;
  }
  return entry?.data;
}

function cacheSet(key, data) {
  _cache?.set(key, { data, ts: Date.now() });
}

// ─── Helpers ────────────────────────────────────────────────────────────────
async function getCompanyId() {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) return null;
    const { data } = await supabase?.from('user_profiles')?.select('company_id')?.eq('id', user?.id)?.maybeSingle();
    return data?.company_id || null;
  } catch {
    return null;
  }
}

async function getUserId() {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    return user?.id || null;
  } catch {
    return null;
  }
}

// ─── Llamada al proveedor de IA (OpenAI / Gemini / fallback) ────────────────
async function callAI(prompt) {
  // Intentar OpenAI primero
  const openaiKey = import.meta.env?.VITE_OPENAI_API_KEY;
  if (openaiKey && !openaiKey?.includes('your-')) {
    try {
      const res = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${openaiKey}`,
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [{ role: 'user', content: prompt }],
          temperature: 0.3,
          max_tokens: 1200,
        }),
        signal: AbortSignal.timeout(20000),
      });
      if (res?.ok) {
        const json = await res?.json();
        return json?.choices?.[0]?.message?.content || '';
      }
    } catch (e) {
      console.warn('[EVA] OpenAI falló, intentando Gemini:', e?.message);
    }
  }

  // Intentar Gemini
  const geminiKey = import.meta.env?.VITE_GEMINI_API_KEY;
  if (geminiKey && !geminiKey?.includes('your-')) {
    try {
      const res = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash:generateContent?key=${geminiKey}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contents: [{ parts: [{ text: prompt }] }],
            generationConfig: { temperature: 0.3, maxOutputTokens: 1200 },
          }),
          signal: AbortSignal.timeout(20000),
        }
      );
      if (res?.ok) {
        const json = await res?.json();
        return json?.candidates?.[0]?.content?.parts?.[0]?.text || '';
      }
    } catch (e) {
      console.warn('[EVA] Gemini falló:', e?.message);
    }
  }

  throw new Error('EVA_UNAVAILABLE');
}

// ─── Parsear JSON de respuesta IA ───────────────────────────────────────────
function parseAIJson(text) {
  try {
    const match = text?.match(/```json\s*([\s\S]*?)```/) ||
                  text?.match(/```\s*([\s\S]*?)```/) ||
                  text?.match(/(\{[\s\S]*\})/);
    const raw = match ? match?.[1] : text;
    return JSON.parse(raw?.trim());
  } catch {
    return null;
  }
}

// ─── Guardar request en DB para auditoría ───────────────────────────────────
async function logRequest(intencion, inputData, responseData, cacheKey, cached) {
  try {
    const companyId = await getCompanyId();
    const userId = await getUserId();
    await supabase?.from('eva_requests')?.insert({
      company_id: companyId,
      intencion,
      input_data: inputData,
      response_data: responseData,
      cache_key: cacheKey,
      cached,
      created_by: userId,
    });
  } catch {
    // No bloquear flujo si falla el log
  }
}

// ─── INTENCIÓN: generar_presupuesto ─────────────────────────────────────────
async function generarPresupuesto({ cliente, vehiculo, hallazgos, modulo }) {
  const cacheKey = `presupuesto:${vehiculo?.vin || vehiculo?.marca}:${JSON.stringify(hallazgos)?.slice(0, 80)}`;
  const cached = cacheGet(cacheKey);
  if (cached) {
    await logRequest('generar_presupuesto', { cliente, vehiculo, hallazgos }, cached, cacheKey, true);
    return { ...cached, fromCache: true };
  }

  const vehiculoStr = vehiculo
    ? `${vehiculo?.marca || ''} ${vehiculo?.modelo || ''} ${vehiculo?.year || ''} (VIN: ${vehiculo?.vin || 'N/D'}, Motor: ${vehiculo?.motor || 'N/D'})`
    : 'Vehículo no especificado';

  const hallazgosStr = Array.isArray(hallazgos)
    ? hallazgos?.map(h => `- ${h?.categoria || ''}: ${h?.descripcion || h?.finding || ''}`)?.join('\n')
    : (hallazgos || 'Sin hallazgos específicos');

  const prompt = `Eres un asesor de taller automotriz experto. Genera un BORRADOR de presupuesto basado en los siguientes hallazgos de inspección.

Cliente: ${cliente?.nombre || 'N/D'}
Vehículo: ${vehiculoStr}
Módulo: ${modulo || 'Cotización'}

Hallazgos:
${hallazgosStr}

Genera un presupuesto con partidas detalladas. Responde SOLO en JSON con esta estructura:
{
  "texto": "Resumen ejecutivo del presupuesto en 2-3 oraciones",
  "partidas_sugeridas": [
    {
      "descripcion": "nombre del servicio o refacción",
      "tipo": "mano_obra" | "refaccion" | "llanta",
      "cantidad": 1,
      "precio_unitario": 0,
      "subtotal": 0,
      "notas": "nota técnica opcional"
    }
  ],
  "totales_sugeridos": {
    "subtotal": 0,
    "iva": 0,
    "total": 0
  },
  "acciones_recomendadas": ["acción 1", "acción 2"]
}`;

  const aiText = await callAI(prompt);
  const parsed = parseAIJson(aiText);

  let result = parsed || {
    texto: 'Presupuesto generado basado en los hallazgos de inspección.',
    partidas_sugeridas: hallazgos?.map?.((h, i) => ({
      descripcion: h?.descripcion || h?.finding || `Servicio ${i + 1}`,
      tipo: 'mano_obra',
      cantidad: 1,
      precio_unitario: 0,
      subtotal: 0,
      notas: '',
    })) || [],
    totales_sugeridos: { subtotal: 0, iva: 0, total: 0 },
    acciones_recomendadas: ['Revisar precios con proveedor', 'Confirmar con cliente'],
  };

  cacheSet(cacheKey, result);
  await logRequest('generar_presupuesto', { cliente, vehiculo, hallazgos }, result, cacheKey, false);
  return result;
}

// ─── INTENCIÓN: agendar_servicio ────────────────────────────────────────────
async function agendarServicio({ cliente, vehiculo, motivo, fechaSugerida }) {
  const cacheKey = `agenda:${cliente?.id || cliente?.nombre}:${motivo}:${fechaSugerida}`;
  const cached = cacheGet(cacheKey);
  if (cached) return { ...cached, fromCache: true };

  const prompt = `Eres un asistente de taller automotriz. Sugiere slots de tiempo para agendar un servicio.

Cliente: ${cliente?.nombre || 'N/D'}
Vehículo: ${vehiculo?.marca || ''} ${vehiculo?.modelo || ''} ${vehiculo?.year || ''}
Motivo: ${motivo || 'Servicio general'}
Fecha preferida: ${fechaSugerida || 'Lo antes posible'}

Sugiere 3 opciones de horario para los próximos 5 días hábiles. Responde SOLO en JSON:
{
  "texto": "Propuesta de agenda para el servicio",
  "slots_sugeridos": [
    {"fecha": "YYYY-MM-DD", "hora_inicio": "HH:MM", "hora_fin": "HH:MM", "duracion_horas": 2, "nota": ""}
  ],
  "duracion_estimada_horas": 2,
  "acciones_recomendadas": ["Confirmar disponibilidad", "Notificar al cliente"]
}`;

  let result;
  try {
    const aiText = await callAI(prompt);
    result = parseAIJson(aiText);
  } catch {
    // Fallback: generar slots básicos
  }

  if (!result) {
    const hoy = new Date();
    const slots = [];
    for (let i = 1; i <= 3; i++) {
      const d = new Date(hoy);
      d?.setDate(d?.getDate() + i);
      const fechaStr = d?.toISOString()?.split('T')?.[0];
      slots?.push({ fecha: fechaStr, hora_inicio: '09:00', hora_fin: '11:00', duracion_horas: 2, nota: `Opción ${i}` });
    }
    result = {
      texto: `Servicio para ${vehiculo?.marca || 'vehículo'} - ${motivo || 'Servicio general'}`,
      slots_sugeridos: slots,
      duracion_estimada_horas: 2,
      acciones_recomendadas: ['Confirmar con el cliente', 'Bloquear slot en calendario'],
    };
  }

  cacheSet(cacheKey, result);
  await logRequest('agendar_servicio', { cliente, vehiculo, motivo, fechaSugerida }, result, cacheKey, false);
  return result;
}

// ─── INTENCIÓN: precio_llanta ────────────────────────────────────────────────
async function buscarPrecioLlanta({ medida, marcaPreferida, vehiculo }) {
  const cacheKey = `llanta:${medida}:${marcaPreferida || 'any'}`;
  const cached = cacheGet(cacheKey);
  if (cached) return { ...cached, fromCache: true };

  const companyId = await getCompanyId();

  // Buscar en tabla tires_price_list
  let query = supabase?.from('tires_price_list')?.select('*')?.ilike('medida', `%${medida?.trim()}%`);

  if (companyId) query = query?.eq('company_id', companyId);
  if (marcaPreferida) query = query?.ilike('marca', `%${marcaPreferida?.trim()}%`);

  const { data: tiresData, error } = await query?.order('precio_venta', { ascending: true })?.limit(10);

  if (error) throw new Error('Error al buscar llantas: ' + error.message);

  // Si no hay resultados con marca, buscar sin filtro de marca
  let allTires = tiresData || [];
  if (allTires?.length === 0 && marcaPreferida) {
    const { data: fallback } = await supabase?.from('tires_price_list')?.select('*')?.ilike('medida', `%${medida?.trim()}%`)?.order('precio_venta', { ascending: true })?.limit(10);
    allTires = fallback || [];
  }

  if (allTires?.length === 0) {
    let result = {
      texto: `No se encontraron llantas con medida ${medida} en el catálogo. Verifica la medida o actualiza el inventario.`,
      opciones: [],
      texto_whatsapp: `No hay llantas ${medida} disponibles en inventario actualmente.`,
      acciones_recomendadas: ['Actualizar catálogo de llantas', 'Consultar con proveedor'],
    };
    await logRequest('precio_llanta', { medida, marcaPreferida }, result, cacheKey, false);
    return result;
  }

  // Seleccionar las 3 mejores opciones
  const top3 = allTires?.slice(0, 3);
  const opcionesTexto = top3?.map((t, i) => `${i + 1}. ${t?.marca} ${t?.modelo || ''} ${t?.medida} — $${t?.precio_venta?.toLocaleString('es-MX')} (Existencia: ${t?.existencia || 0} pzas, Proveedor: ${t?.proveedor || 'N/D'})`)?.join('\n');

  const vehiculoStr = vehiculo ? `${vehiculo?.marca || ''} ${vehiculo?.modelo || ''} ${vehiculo?.year || ''}`?.trim() : '';

  const textoWhatsApp = `🔧 *Cotización de Llantas*\n\n` +
    (vehiculoStr ? `Vehículo: ${vehiculoStr}\n` : '') +
    `Medida: *${medida}*\n\n` +
    `Opciones disponibles:\n${opcionesTexto}\n\n` +
    `¿Le interesa alguna opción? Responda con el número de su preferencia.`;

  let result = {
    texto: `Se encontraron ${allTires?.length} llantas con medida ${medida}. Mostrando las 3 mejores opciones.`,
    opciones: top3?.map(t => ({
      id: t?.id,
      medida: t?.medida,
      marca: t?.marca,
      modelo: t?.modelo,
      precio_venta: t?.precio_venta,
      costo: t?.costo,
      proveedor: t?.proveedor,
      existencia: t?.existencia,
    })),
    texto_whatsapp: textoWhatsApp,
    acciones_recomendadas: ['Insertar en cotización', 'Enviar por WhatsApp al cliente'],
  };

  cacheSet(cacheKey, result);
  await logRequest('precio_llanta', { medida, marcaPreferida, vehiculo }, result, cacheKey, false);
  return result;
}

// ─── INTENCIÓN: mensaje_whatsapp ─────────────────────────────────────────────
async function generarMensajeWhatsApp({ cliente, vehiculo, partidas, totales, tipo }) {
  const tipoMsg = tipo || 'cotizacion';
  const cacheKey = `wa:${tipoMsg}:${cliente?.id || cliente?.nombre}:${totales?.total}`;
  const cached = cacheGet(cacheKey);
  if (cached) return { ...cached, fromCache: true };

  const vehiculoStr = vehiculo
    ? `${vehiculo?.marca || ''} ${vehiculo?.modelo || ''} ${vehiculo?.year || ''}`?.trim()
    : 'su vehículo';

  const listaServicios = Array.isArray(partidas)
    ? partidas?.map(p => `• ${p?.descripcion} — $${Number(p?.subtotal || p?.precio_unitario || 0)?.toLocaleString('es-MX')}`)?.join('\n')
    : '';

  let texto;
  if (tipoMsg === 'cotizacion') {
    texto = `Hola ${cliente?.nombre || 'estimado cliente'} 👋\n\n` +
      `Hemos realizado la inspección de su vehículo:\n` +
      `🚗 *Vehículo:* ${vehiculoStr}\n` +
      (vehiculo?.placas ? `🔖 *Placas:* ${vehiculo?.placas}\n` : '') +
      `\nLe compartimos la cotización de reparación:\n\n` +
      `${listaServicios}\n\n` +
      `💰 *Total estimado: $${Number(totales?.total || 0)?.toLocaleString('es-MX')} MXN*\n\n` +
      `¿Desea autorizar la reparación?\n\n` +
      `1️⃣ Autorizar\n2️⃣ Consultar\n3️⃣ Rechazar`;
  } else {
    texto = `Hola ${cliente?.nombre || 'estimado cliente'} 👋\n\nInformación de su servicio:\n${listaServicios}`;
  }

  let result = {
    texto,
    acciones_recomendadas: ['Copiar mensaje', 'Abrir WhatsApp'],
  };

  cacheSet(cacheKey, result);
  await logRequest('mensaje_whatsapp', { cliente, vehiculo, tipo }, result, cacheKey, false);
  return result;
}

// ─── INTENCIÓN: email_presupuesto ────────────────────────────────────────────
async function generarEmailPresupuesto({ cliente, vehiculo, partidas, totales, nombreTaller }) {
  const cacheKey = `email:${cliente?.id || cliente?.nombre}:${totales?.total}`;
  const cached = cacheGet(cacheKey);
  if (cached) return { ...cached, fromCache: true };

  const vehiculoStr = vehiculo
    ? `${vehiculo?.marca || ''} ${vehiculo?.modelo || ''} ${vehiculo?.year || ''}`?.trim()
    : 'su vehículo';

  const listaHtml = Array.isArray(partidas)
    ? partidas?.map(p =>
        `<tr><td style="padding:8px;border:1px solid #e5e7eb">${p?.descripcion}</td><td style="padding:8px;border:1px solid #e5e7eb;text-align:center">${p?.cantidad || 1}</td><td style="padding:8px;border:1px solid #e5e7eb;text-align:right">$${Number(p?.precio_unitario || 0)?.toLocaleString('es-MX')}</td><td style="padding:8px;border:1px solid #e5e7eb;text-align:right">$${Number(p?.subtotal || 0)?.toLocaleString('es-MX')}</td></tr>`
      )?.join('')
    : '';

  const asunto = `Cotización de Servicio — ${nombreTaller || 'Taller'}`;
  const cuerpoHtml = `<div style="font-family:sans-serif;max-width:600px;margin:0 auto">
  <h2 style="color:#1e40af">${nombreTaller || 'Taller Automotriz'}</h2>
  <p>Estimado/a <strong>${cliente?.nombre || 'cliente'}</strong>,</p>
  <p>Adjuntamos la cotización de servicio para su vehículo <strong>${vehiculoStr}</strong>.</p>
  <table style="width:100%;border-collapse:collapse;margin:16px 0">
    <thead><tr style="background:#f3f4f6">
      <th style="padding:8px;border:1px solid #e5e7eb;text-align:left">Descripción</th>
      <th style="padding:8px;border:1px solid #e5e7eb">Cant.</th>
      <th style="padding:8px;border:1px solid #e5e7eb">P. Unit.</th>
      <th style="padding:8px;border:1px solid #e5e7eb">Subtotal</th>
    </tr></thead>
    <tbody>${listaHtml}</tbody>
  </table>
  <p style="text-align:right"><strong>Subtotal: $${Number(totales?.subtotal || 0)?.toLocaleString('es-MX')}</strong></p>
  <p style="text-align:right">IVA (16%): $${Number(totales?.iva || 0)?.toLocaleString('es-MX')}</p>
  <p style="text-align:right;font-size:1.2em"><strong>Total: $${Number(totales?.total || 0)?.toLocaleString('es-MX')} MXN</strong></p>
  <p>Para autorizar, consultar o rechazar esta cotización, por favor contáctenos.</p>
</div>`;

  let result = {
    asunto,
    cuerpo_html: cuerpoHtml,
    texto: `Email de cotización listo para enviar a ${cliente?.email || cliente?.nombre}.`,
    acciones_recomendadas: ['Enviar por email', 'Copiar contenido'],
  };

  cacheSet(cacheKey, result);
  await logRequest('email_presupuesto', { cliente, vehiculo }, result, cacheKey, false);
  return result;
}

// ─── GUARDAR CITA EN calendar_events ────────────────────────────────────────
async function guardarCita({ clienteId, vehiculoId, fechaHora, motivo, notas, duracionHoras }) {
  const companyId = await getCompanyId();
  const userId = await getUserId();

  const startDt = new Date(fechaHora);
  const endDt = new Date(startDt.getTime() + (duracionHoras || 2) * 60 * 60 * 1000);

  const { data, error } = await supabase?.from('calendar_events')?.insert({
      company_id: companyId,
      title: motivo || 'Servicio agendado',
      description: notas || '',
      related_type: 'inspection',
      client_id: clienteId || null,
      vehicle_id: vehiculoId || null,
      start_datetime: startDt?.toISOString(),
      end_datetime: endDt?.toISOString(),
      assigned_to: userId,
      status: 'scheduled',
      color: '#3B82F6',
      created_by: userId,
    })?.select()?.single();

  if (error) throw new Error('Error al guardar cita: ' + error.message);
  return data;
}

// ─── GUARDAR EN OUTBOX ───────────────────────────────────────────────────────
async function guardarEnOutbox({ canal, destinatario, asunto, cuerpo, relatedType, relatedId, metadata }) {
  const companyId = await getCompanyId();
  const userId = await getUserId();

  const { data, error } = await supabase?.from('message_outbox')?.insert({
      company_id: companyId,
      channel: canal,
      recipient: destinatario,
      subject: asunto || null,
      body: cuerpo,
      metadata: metadata || {},
      status: 'pending',
      related_type: relatedType || null,
      related_id: relatedId || null,
      created_by: userId,
    })?.select()?.single();

  if (error) throw new Error('Error al guardar en outbox: ' + error.message);
  return data;
}

// ─── FUNCIÓN PRINCIPAL: procesar intención ───────────────────────────────────
async function procesarIntencion({ intencion, contexto, datos }) {
  try {
    switch (intencion) {
      case 'generar_presupuesto':
        return await generarPresupuesto(datos || {});
      case 'agendar_servicio':
        return await agendarServicio(datos || {});
      case 'precio_llanta':
        return await buscarPrecioLlanta(datos || {});
      case 'mensaje_whatsapp':
        return await generarMensajeWhatsApp(datos || {});
      case 'email_presupuesto':
        return await generarEmailPresupuesto(datos || {});
      default:
        return {
          texto: `Intención "${intencion}" no reconocida. Las opciones disponibles son: generar_presupuesto, agendar_servicio, precio_llanta, mensaje_whatsapp, email_presupuesto.`,
          partidas_sugeridas: [],
          acciones_recomendadas: [],
        };
    }
  } catch (err) {
    if (err?.message === 'EVA_UNAVAILABLE') {
      return {
        texto: 'EVA no disponible en este momento. Por favor intenta más tarde.',
        error: true,
        partidas_sugeridas: [],
        acciones_recomendadas: ['Reintentar en unos minutos'],
      };
    }
    console.error('[EVA] Error procesando intención:', err);
    return {
      texto: 'Ocurrió un error al procesar tu solicitud. Por favor intenta de nuevo.',
      error: true,
      partidas_sugeridas: [],
      acciones_recomendadas: [],
    };
  }
}

export const evaService = {
  procesarIntencion,
  generarPresupuesto,
  agendarServicio,
  buscarPrecioLlanta,
  generarMensajeWhatsApp,
  generarEmailPresupuesto,
  guardarCita,
  guardarEnOutbox,
};

export default evaService;
